import { useEffect, useState } from 'react';
import { api } from '../api/endpoints';
import type { CustomerCard, PaymentMode, Pool, Pricing, PricingPack } from '../api/types';
import {
  formatMinutes,
  formatMoney,
  POOL_LABEL,
  POOL_LABEL_INLINE,
} from '../lib/format';
import { PaymentModePicker, SplitFields } from './CustomerTab';
import { useToast } from './Toast';
import { Button, ErrorNote, Input, Modal, Spinner } from './ui';

// Staff pick a pack; price, minutes and validity all come from the server, so a
// price change never needs a frontend release. The override fields are for the
// occasional negotiated sale and stay behind "Adjust".
//
// "Start later" sells a pack on hold — a gift, or one bought ahead of the
// weekend. It is paid right here (a held pack is never left on the tab, since
// nothing would remind anyone it is owed), and an admin activates it on the
// day the customer starts; the validity counts from then.
//
// Opened from a customer's app request, the pack they asked for is picked
// already and the sale carries `requestId`, which marks the request done.
export function SellRechargeDialog({
  customer,
  initialPool,
  initialPackId,
  requestId,
  onClose,
  onSold,
}: {
  customer: CustomerCard;
  initialPool: Pool;
  initialPackId?: string;
  requestId?: string;
  onClose: () => void;
  onSold: () => void;
}) {
  const toast = useToast();
  const [pricing, setPricing] = useState<Pricing | null>(null);
  const [pool, setPool] = useState<Pool>(initialPool);
  const [selected, setSelected] = useState<PricingPack | null>(null);
  const [adjusting, setAdjusting] = useState(false);
  const [amountPaid, setAmountPaid] = useState('');
  const [minutes, setMinutes] = useState('');
  const [onHold, setOnHold] = useState(false);
  const [mode, setMode] = useState<PaymentMode>('cash');
  const [useCredit, setUseCredit] = useState(true);
  const [cashPart, setCashPart] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api.recharges
      .pricing(controller.signal)
      .then((loaded) => {
        setPricing(loaded);
        if (initialPackId) {
          const pack = [...loaded.general_packs, ...loaded.simulator_packs].find(
            (candidate) => candidate.pricing_rule_id === initialPackId,
          );
          if (pack) setSelected(pack);
        }
      })
      .catch((err) => {
        if ((err as Error).name !== 'AbortError') setError((err as Error).message);
      });
    return () => controller.abort();
  }, []);

  const packs =
    pool === 'simulator' ? pricing?.simulator_packs : pricing?.general_packs;

  // What a held pack collects now: its price, less stored credit if the
  // customer wants it used — their choice; some keep credit for food.
  const price =
    selected && adjusting && amountPaid.trim() !== '' ? Number(amountPaid) : Number(selected?.price ?? 0);
  const creditAvailable = customer.credit_balance ?? 0;
  const creditApplied = useCredit ? Math.min(creditAvailable, price) : 0;
  const toCollect = Math.round((price - creditApplied) * 100) / 100;
  const cashPartValue = cashPart.trim() === '' ? 0 : Number(cashPart);
  const upiPartValue = Math.round((toCollect - cashPartValue) * 100) / 100;
  const splitValid =
    !onHold || mode !== 'split' || (cashPart.trim() !== '' && cashPartValue >= 0 && upiPartValue >= 0);

  const sell = async () => {
    if (!selected || !splitValid) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.recharges.sell({
        customer_id: customer.customer_id,
        pricing_rule_id: selected.pricing_rule_id,
        amount_paid: adjusting && amountPaid ? Number(amountPaid) : undefined,
        minutes: adjusting && minutes ? Number(minutes) : undefined,
        request_id: requestId,
        ...(onHold
          ? {
              on_hold: true,
              payment_mode: mode,
              use_credit: useCredit,
              ...(mode === 'split' ? { cash_amount: cashPartValue, upi_amount: upiPartValue } : {}),
            }
          : {}),
      });

      if (onHold) {
        toast.success(
          `${selected.label} sold to ${customer.name} on hold — ${formatMoney(toCollect)} received` +
            `${creditApplied > 0 ? `, ${formatMoney(creditApplied)} from credit` : ''}. ` +
            'An admin activates it when they start playing.',
        );
        onSold();
        onClose();
        return;
      }

      const balance =
        pool === 'simulator'
          ? result.wallet.simulator_minutes
          : result.wallet.general_minutes;

      // A pack sold mid-session is picked up by the engine within one tick, so
      // staff should not go hunting for a resume button that does not exist.
      // The money is not taken here — the pack's price went on the tab.
      const owed =
        result.due_amount !== undefined ? ` ${formatMoney(result.due_amount)} on their tab.` : '';
      toast.success(
        customer.active_session
          ? `${selected.label} added — ${customer.name} now has ${formatMinutes(balance)}. Their session continues automatically.${owed}`
          : `${selected.label} added — ${customer.name} now has ${formatMinutes(balance)}.${owed}`,
      );
      onSold();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Recharge for ${customer.name}`} onClose={onClose} wide>
      <div className="space-y-4">
        <div className="flex gap-2">
          {(['general', 'simulator'] as Pool[]).map((option) => (
            <Button
              key={option}
              variant={pool === option ? 'primary' : 'secondary'}
              onClick={() => {
                setPool(option);
                setSelected(null);
              }}
            >
              {POOL_LABEL[option]}
            </Button>
          ))}
        </div>

        <p className="text-xs text-slate-500">
          These minutes only work on {POOL_LABEL_INLINE[pool]} stations —
          the two pools never mix.
        </p>

        {error && <ErrorNote>{error}</ErrorNote>}

        {!pricing ? (
          <Spinner label="Loading the rate card…" />
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {packs?.map((pack) => {
              const active = selected?.pricing_rule_id === pack.pricing_rule_id;
              return (
                <button
                  key={pack.pricing_rule_id}
                  type="button"
                  onClick={() => setSelected(pack)}
                  className={`rounded-xl border p-3 text-left transition-colors ${
                    active
                      ? 'border-sky-500 bg-sky-500/15'
                      : 'border-slate-800 bg-slate-900 hover:border-slate-600'
                  }`}
                >
                  <p className="font-medium text-slate-100">{pack.label}</p>
                  <p className="text-sm text-slate-300">{formatMoney(pack.price)}</p>
                  <p className="text-xs text-slate-500">
                    {formatMinutes(pack.minutes)} · valid {pack.validity_days} days
                  </p>
                </button>
              );
            })}
            {packs?.length === 0 && (
              <p className="text-sm text-slate-500">
                No packs configured for this pool.
              </p>
            )}
          </div>
        )}

        {selected && (
          <div className="space-y-3 rounded-xl border border-slate-800 p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-slate-300">
                {selected.label} — {formatMoney(selected.price)} for{' '}
                {formatMinutes(selected.minutes)}
              </p>
              <Button variant="ghost" onClick={() => setAdjusting((on) => !on)}>
                {adjusting ? 'Use pack price' : 'Adjust'}
              </Button>
            </div>

            {adjusting && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input
                  label="Price (₹)"
                  hint="Goes on the tab; collected when the tab is settled."
                  value={amountPaid}
                  onChange={(event) => setAmountPaid(event.target.value)}
                  inputMode="decimal"
                  placeholder={String(selected.price)}
                />
                <Input
                  label="Minutes"
                  value={minutes}
                  onChange={(event) => setMinutes(event.target.value)}
                  inputMode="numeric"
                  placeholder={String(selected.minutes)}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              {([false, true] as const).map((hold) => (
                <button
                  key={String(hold)}
                  type="button"
                  onClick={() => setOnHold(hold)}
                  className={`min-h-11 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                    onHold === hold
                      ? 'border-sky-500/60 bg-sky-500/15 text-sky-300'
                      : 'border-slate-800 text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  {hold ? 'Start later (on hold)' : 'Start now'}
                </button>
              ))}
            </div>

            {onHold ? (
              <div className="space-y-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                <p className="text-sm text-amber-200">
                  Paid now, in full. The {formatMinutes(adjusting && minutes ? Number(minutes) : selected.minutes)}{' '}
                  cannot be played and the {selected.validity_days}-day validity does not start
                  until an admin activates it.
                </p>
                {creditAvailable > 0 && (
                  <div>
                    <span className="mb-1.5 block text-sm font-medium text-slate-300">
                      {customer.name} has {formatMoney(creditAvailable)} credit
                    </span>
                    <div className="grid grid-cols-2 gap-2">
                      {([true, false] as const).map((option) => (
                        <button
                          key={String(option)}
                          type="button"
                          onClick={() => setUseCredit(option)}
                          className={`min-h-11 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                            useCredit === option
                              ? 'border-sky-500/60 bg-sky-500/15 text-sky-300'
                              : 'border-slate-800 text-slate-300 hover:bg-slate-800'
                          }`}
                        >
                          {option ? 'Use credit' : 'Keep credit'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="text-sm text-slate-300">
                  <p>Price: {formatMoney(price)}</p>
                  {creditApplied > 0 && <p>From credit: −{formatMoney(creditApplied)}</p>}
                  <p className="font-semibold text-slate-100">Collect now: {formatMoney(toCollect)}</p>
                </div>
                <PaymentModePicker mode={mode} onChange={setMode} />
                {mode === 'split' && (
                  <SplitFields cashPart={cashPart} onCashPart={setCashPart} upiPart={upiPartValue} />
                )}
              </div>
            ) : (
              <p className="text-xs text-slate-500">
                The pack's price goes on {customer.name}'s tab and is collected when the
                tab is settled — nothing is marked as received now.
              </p>
            )}
            <Button className="w-full" onClick={sell} disabled={busy || !splitValid}>
              {busy
                ? 'Adding…'
                : onHold
                  ? `Sell ${selected.label} on hold · collect ${formatMoney(toCollect)}`
                  : `Add ${selected.label} to tab`}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
